/*
  Jersey hero (sections/jersey-hero.liquid)

  Autoplay is driven by the active tab's progress-bar CSS animation: when it
  finishes, the next slide shows. Pausing just pauses that animation, so a
  hover resumes from where it left off instead of restarting the timer.
*/
if (!customElements.get('jersey-hero')) {
  class JerseyHero extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('.jh-slide'));
      this.tabs = Array.from(this.querySelectorAll('.jh-tab'));
      if (this.slides.length < 2) return;

      this.index = 0;
      this.pauseReasons = new Set();
      this.desktop = window.matchMedia('(min-width: 769px)');
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.pauseButton = this.querySelector('[data-jh-pause]');
      this.abort = new AbortController();
      const opts = { signal: this.abort.signal };

      this.tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => this.go(i), opts);
        tab.querySelector('.jh-tab__progress').addEventListener('animationend', () => this.next(), opts);
      });
      this.querySelector('.jh-tabs').addEventListener('keydown', (e) => this.onTabKeydown(e), opts);
      this.querySelector('[data-jh-prev]').addEventListener('click', () => this.prev(), opts);
      this.querySelector('[data-jh-next]').addEventListener('click', () => this.next(), opts);
      this.pauseButton.addEventListener('click', () => this.toggleUserPause(), opts);

      this.addEventListener('mouseenter', () => this.pause('hover'), opts);
      this.addEventListener('mouseleave', () => this.resume('hover'), opts);
      // Only keyboard focus pauses; a mouse click on a tab shouldn't stop rotation.
      this.addEventListener('focusin', (e) => {
        if (e.target.matches(':focus-visible')) this.pause('focus');
      }, opts);
      this.addEventListener('focusout', (e) => {
        if (!this.contains(e.relatedTarget)) this.resume('focus');
      }, opts);
      document.addEventListener('visibilitychange', () => {
        document.hidden ? this.pause('hidden') : this.resume('hidden');
      }, opts);

      this.addSwipe(opts);

      // Theme editor: show the selected block and hold it there.
      document.addEventListener('shopify:block:select', (e) => {
        const slide = e.target.closest && e.target.closest('.jh-slide');
        const i = this.slides.indexOf(slide);
        if (i === -1) return;
        this.go(i);
        this.pause('editor');
      }, opts);
      document.addEventListener('shopify:block:deselect', (e) => {
        if (this.contains(e.target)) this.resume('editor');
      }, opts);

      // The header sits over the hero (see jersey-hero.css); pad by its height.
      const header = document.querySelector('.header-sticky-wrapper');
      if (header) {
        const setHeaderHeight = () => this.style.setProperty('--jh-header-h', `${header.offsetHeight}px`);
        setHeaderHeight();
        this.headerObserver = new ResizeObserver(setHeaderHeight);
        this.headerObserver.observe(header);
      }

      // Hidden slides' jerseys are lazy; fetch them once the page has loaded so
      // they don't pop in on first switch.
      const loadAll = () => this.querySelectorAll('img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; });
      document.readyState === 'complete' ? loadAll() : window.addEventListener('load', loadAll, { once: true, signal: opts.signal });

      this.desktop.addEventListener('change', () => this.updateAutoplay(), opts);
      this.reducedMotion.addEventListener('change', () => this.updateAutoplay(), opts);
      this.updateAutoplay();
    }

    disconnectedCallback() {
      if (this.abort) this.abort.abort();
      if (this.headerObserver) this.headerObserver.disconnect();
    }

    go(i) {
      const count = this.slides.length;
      const nextIndex = (i + count) % count;
      if (nextIndex === this.index) return;

      // Direction drives which way the parallax layers enter.
      const forward = nextIndex === (this.index + 1) % count;
      this.dataset.direction = forward ? 'next' : 'prev';
      this.dataset.ready = '';

      const current = this.slides[this.index];
      const incoming = this.slides[nextIndex];
      this.slides.forEach((s) => s.classList.remove('is-leaving'));
      current.classList.remove('is-active');
      current.classList.add('is-leaving');
      incoming.classList.add('is-active');
      clearTimeout(this.leaveTimer);
      this.leaveTimer = setTimeout(() => current.classList.remove('is-leaving'), 700);

      this.tabs.forEach((tab, t) => {
        const active = t === nextIndex;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
        tab.tabIndex = active ? 0 : -1;
      });

      this.index = nextIndex;
      this.dataset.activeStyle = incoming.dataset.style;
      document.documentElement.dataset.heroStyle = incoming.dataset.style;
    }

    next() {
      this.go(this.index + 1);
    }

    prev() {
      this.go(this.index - 1);
    }

    onTabKeydown(e) {
      const keys = { ArrowRight: this.index + 1, ArrowLeft: this.index - 1, Home: 0, End: this.slides.length - 1 };
      if (!(e.key in keys)) return;
      e.preventDefault();
      this.go(keys[e.key]);
      this.tabs[this.index].focus();
    }

    addSwipe(opts) {
      const stage = this.querySelector('.jh-stage');
      let startX = null;
      let startY = null;
      stage.addEventListener('touchstart', (e) => {
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
      }, { passive: true, signal: opts.signal });
      stage.addEventListener('touchend', (e) => {
        if (startX === null) return;
        const dx = e.changedTouches[0].clientX - startX;
        const dy = e.changedTouches[0].clientY - startY;
        startX = null;
        if (Math.abs(dx) < 50 || Math.abs(dx) < Math.abs(dy)) return;
        dx < 0 ? this.next() : this.prev();
      }, opts);
    }

    updateAutoplay() {
      const enabled = this.dataset.autoplay === 'true' && this.desktop.matches && !this.reducedMotion.matches;
      this.dataset.autoplayEnabled = enabled;
      this.dataset.playing = enabled && !this.pauseReasons.has('user');
      this.dataset.paused = this.pauseReasons.size > 0;
    }

    pause(reason) {
      this.pauseReasons.add(reason);
      this.updateAutoplay();
    }

    resume(reason) {
      this.pauseReasons.delete(reason);
      this.updateAutoplay();
    }

    toggleUserPause() {
      const paused = !this.pauseReasons.has('user');
      paused ? this.pause('user') : this.resume('user');
      this.pauseButton.setAttribute('aria-pressed', paused);
      this.pauseButton.querySelector('.visually-hidden').textContent = paused ? 'Play slideshow' : 'Pause slideshow';
    }
  }

  customElements.define('jersey-hero', JerseyHero);
}
